/**
 * USR-01/02: Mantenedor de Usuarios y Roles del Tenant
 * Lista usuarios, permite invitar, modificar roles, revocar acceso y gestionar permisos.
 */
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  UserPlus,
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  Trash2,
  Edit2,
  Mail,
  Users,
  KeyRound,
  Send,
} from 'lucide-react'
import { KebabMenu } from '@/components/KebabMenu'
import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import {
  useTenantUsers,
  useInviteUser,
  useUpdateUserRoles,
  useRevokeUserAccess,
  useAdminResetPassword,
  useResendInvitation,
  useRoles,
} from '@/modules/users/hooks/useUsers'
import { useTenantSSOEnabled } from '@/modules/config/hooks/useConfig'
import { RolesTab } from './RolesTab'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/ds/Modal'

// ─── Schema de invitación ─────────────────────────────────────────────────────
const inviteSchema = z.object({
  email: z.string().email('Email inválido'),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  role_ids: z.array(z.number()).min(1, 'Seleccionar al menos un rol'),
})
type InviteFormData = z.infer<typeof inviteSchema>

// ─── Página principal ─────────────────────────────────────────────────────────

const TABS = [
  { key: 'users', label: 'Usuarios', icon: Users },
  { key: 'roles', label: 'Roles y Permisos', icon: KeyRound },
]

export function UsersPage() {
  const [activeTab, setActiveTab] = useState('users')
  const [searchTerm, setSearchTerm] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [editingUser, setEditingUser] = useState<any | null>(null)

  const { data, isLoading, error } = useTenantUsers()

  const filteredUsers = data?.users.filter(
    (u) =>
      u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  ) ?? []

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Usuarios y Roles"
        subtitle="Gestión del acceso y los permisos de la empresa"
        help={HELP['users']}
      >
        {activeTab === 'users' && (
          <Button onClick={() => setShowInviteModal(true)} leftIcon={<UserPlus size={16} />}>
            Invitar usuario
          </Button>
        )}
      </PageHeader>

      {/* Tabs */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden mb-6 shadow-card">
        <div className="flex border-b border-divider">
          {TABS.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors border-b-2
                  ${activeTab === tab.key
                    ? 'border-primary text-primary-hover bg-primary-50/50'
                    : 'border-transparent text-content-muted hover:text-content hover:bg-soft'
                  }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="p-6">
          {activeTab === 'roles' ? (
            <RolesTab />
          ) : (
            <>
              {/* Buscador */}
              <div className="relative mb-4">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
                <input aria-label="Buscar por nombre o email"
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-divider rounded-lg text-sm
                    text-content placeholder:text-content-subtle outline-none focus:ring-2
                    focus:ring-primary focus:border-primary bg-surface"
                />
              </div>

              {/* Tabla de usuarios. El panel lo pone el DataTable: envolverlo
                  en uno propio da dos bordes (lo vigila `superficieUnica`). */}
              {error ? (
                <div className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-sm text-center py-12 text-danger-solid text-sm">
                  Error al cargar los usuarios. Intenta recargar.
                </div>
              ) : (
                <DataTable
                  columns={columnasDeUsuario({ onEditRoles: setEditingUser })}
                  rows={filteredUsers}
                  rowKey={(user: any) => user.id}
                  onRowClick={(user: any) => setEditingUser(user)}
                  loading={isLoading}
                  loadingNode={
                    <div className="flex items-center justify-center py-12 text-content-subtle">
                      <Loader2 size={20} className="animate-spin mr-2" />
                      Cargando usuarios...
                    </div>
                  }
                  empty={
                    <EmptyState
                      title={searchTerm ? 'Sin resultados para tu búsqueda.' : 'No hay usuarios aún. Invita al primero.'}
                      size="sm"
                    />
                  }
                  minWidth={820}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Modales */}
      {showInviteModal && (
        <InviteUserModal onClose={() => setShowInviteModal(false)} />
      )}
      {editingUser && (
        <EditRolesModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  )
}

// ─── Fila de usuario ──────────────────────────────────────────────────────────
const ESTADO_DE_MEMBRESIA = {
  accepted: { label: 'Activo', icon: CheckCircle2, color: 'text-success-content-soft bg-success-surface' },
  pending: { label: 'Pendiente', icon: Clock, color: 'text-warning-content-soft bg-warning-surface' },
  revoked: { label: 'Revocado', icon: XCircle, color: 'text-content-subtle bg-surface-muted' },
}

/**
 * Las columnas del listado de usuarios.
 *
 * 🔴 Esto era `UserRow`, que el inventario del barrido daba por **bloqueo
 * real**: tenía `useState` y tres mutaciones adentro. Al mirarlo, los hooks no
 * son de la fila — son **de una sola celda**, la de acciones. Las otras cuatro
 * columnas son render puro.
 *
 * Por eso se desarma igual: las cuatro pasan a `render`, y la quinta delega en
 * `AccionesDeUsuario`, que conserva sus hooks por ser un componente. Es el
 * mismo movimiento que resolvió `TenantLocalizationRow` en el platform admin.
 */
function columnasDeUsuario({ onEditRoles }: {
  onEditRoles: (user: any) => void
}): DataTableColumn<any>[] {
  return [
    { key: 'full_name', header: 'Usuario', sortable: true,
      sortValue: user => user.full_name || user.email,
      render: user => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary-hover
            flex items-center justify-center text-white text-sm font-semibold shrink-0">
            {user.full_name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-content">{user.full_name || '—'}</p>
            <p className="text-xs text-content-subtle flex items-center gap-1">
              <Mail size={11} />
              {user.email}
            </p>
          </div>
        </div>
      ) },
    { key: 'roles', header: 'Roles',
      render: user => (
        <div className="flex flex-wrap gap-1">
          {user.roles.map((role: any) => (
            <span
              key={role.id ?? role.type}
              className={`text-xs px-2 py-0.5 rounded-full font-medium
                ${role.type === 'admin' ? 'bg-warning-surface text-warning-content' : 'bg-primary-50 text-primary-hover'}`}
            >
              {role.type === 'admin' && <Shield size={10} className="inline mr-0.5" />}
              {role.name}
            </span>
          ))}
        </div>
      ) },
    { key: 'membership_status', header: 'Estado', sortable: true,
      render: user => {
        const info = ESTADO_DE_MEMBRESIA[user.membership_status as keyof typeof ESTADO_DE_MEMBRESIA]
        if (!info) return <span className="text-content-subtle">—</span>
        const Icono = info.icon
        return (
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${info.color}`}>
            <Icono size={12} />
            {info.label}
          </span>
        )
      } },
    { key: 'last_seen_at', header: 'Último acceso', sortable: true,
      // ⚠️ Se ordena por la fecha cruda: el texto formateado ordenaría
      // «02-01-2020» antes que «15-09-2026», y «Invitación pendiente» iría
      // entre medio de las fechas.
      sortValue: user => (user.last_seen_at ? new Date(user.last_seen_at) : null),
      render: user => (
        <span className="text-content-subtle">
          {user.last_seen_at
            ? new Date(user.last_seen_at).toLocaleDateString('es-CL')
            : user.membership_status === 'pending' ? 'Invitación pendiente' : '—'}
        </span>
      ) },
    { key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 140,
      render: user => <AccionesDeUsuario user={user} onEditRoles={onEditRoles} /> },
  ]
}

/**
 * La celda de acciones: es la que tiene los hooks.
 *
 * ⚠️ Su `onClick` detiene la propagación porque la fila entera abre el modal de
 * roles: sin eso, abrir el menú o confirmar un reseteo abriría además el modal
 * detrás.
 */
function AccionesDeUsuario({ user, onEditRoles }: {
  user: any
  onEditRoles: (user: any) => void
}) {
  const [resetMsg, setResetMsg] = useState<string | null>(null)
  const { mutate: revokeAccess, isPending: isRevoking } = useRevokeUserAccess()
  const { mutate: resetPassword, isPending: isResetting } = useAdminResetPassword()
  const { mutate: resendInvitation, isPending: isResending } = useResendInvitation()
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  return (
    <div onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={() => onEditRoles(user)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
          title="Modificar roles"
        >
          <Edit2 size={11} />Roles
        </button>
        <KebabMenu items={[
            ...(user.membership_status === 'pending' ? [{
              label: 'Reenviar invitación',
              icon: <Send size={14} />,
              onClick: () => { confirmAction({ message: `¿Reenviar invitación a ${user.email}?`, variant: 'warning', onConfirm: () => { resendInvitation(user.id, { onSuccess: (data) => { setResetMsg(data.detail); setTimeout(() => setResetMsg(null), 5000) }, onError: (err: any) => { const msg = err?.response?.data?.error?.message || 'Error al reenviar la invitación.'; setResetMsg(msg); setTimeout(() => setResetMsg(null), 5000) } }) } }) },
              disabled: isResending,
            }] : []),
            { label: 'Resetear contraseña', icon: <KeyRound size={14} />, onClick: () => { confirmAction({ message: `¿Enviar email de reseteo de contraseña a ${user.email}?`, variant: 'warning', onConfirm: () => { resetPassword(user.id, { onSuccess: (data) => { setResetMsg(data.detail); setTimeout(() => setResetMsg(null), 5000) }, onError: (err: any) => { const msg = err?.response?.data?.error?.message || 'Error al enviar el reseteo.'; setResetMsg(msg); setTimeout(() => setResetMsg(null), 5000) } }) } }) }, disabled: isResetting },
            { label: 'Revocar acceso', icon: <Trash2 size={14} />, onClick: () => { confirmAction({ message: '¿Confirmas que deseas revocar el acceso de este usuario?', variant: 'danger', onConfirm: () => { revokeAccess(user.id) } }) }, className: 'text-danger-content-soft hover:bg-danger-surface', disabled: isRevoking },
          ]} />
        </div>
      {resetMsg && (
        <div
          className="fixed bottom-4 right-4 z-[100] bg-popover border border-divider shadow-lg rounded-lg px-4 py-3 text-sm text-content max-w-sm"
          style={{ backgroundColor: 'rgb(var(--color-popover))' }}
        >
          {resetMsg}
        </div>
      )}
      <ConfirmModal {...modalProps} />
    </div>
  )
}

// ─── Modal de Edición de Roles ────────────────────────────────────────────────
function EditRolesModal({ user, onClose }: { user: any; onClose: () => void }) {
  const { data: rolesData } = useRoles()
  const { mutate: updateRoles, isPending } = useUpdateUserRoles()
  // Se identifican los roles por su id (no por role_type): dos roles distintos
  // pueden compartir el mismo tipo base (ej. "Vendedor" + "Facturador") y no
  // deben acoplarse al asignarlos/quitarlos.
  const [selectedRoles, setSelectedRoles] = useState<number[]>(
    user.roles.map((r: any) => r.id).filter((id: any) => id != null)
  )
  const [error, setError] = useState<string | null>(null)

  const toggleRole = (roleId: number) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]
    )
  }

  const handleSubmit = () => {
    if (selectedRoles.length === 0) {
      setError('Debes asignar al menos un rol.')
      return
    }
    setError(null)
    updateRoles(
      { userId: user.id, roleIds: selectedRoles },
      {
        onSuccess: onClose,
        onError: (err: any) => {
          setError(err?.response?.data?.error?.message || 'Error al actualizar los roles.')
        },
      }
    )
  }

  return (
    <Modal open onClose={onClose} title="Modificar roles" subtitle={user.full_name || user.email} size="md" dismissOnScrimClick={false}>
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-danger-surface border border-danger-border rounded-lg text-danger-content text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(rolesData || []).map((role) => (
            <label
              key={role.id}
              className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors
                ${selectedRoles.includes(role.id)
                  ? 'border-primary bg-primary-50'
                  : 'border-divider hover:border-divider-strong hover:bg-soft'
                }`}
            >
              <input
                type="checkbox"
                checked={selectedRoles.includes(role.id)}
                onChange={() => toggleRole(role.id)}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm text-content">{role.name}</span>
            </label>
          ))}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-divider text-content-muted rounded-lg text-sm font-medium hover:bg-soft transition-colors"
          >
            Cancelar
          </button>
          <Button onClick={handleSubmit} size="lg" fullWidth isPending={isPending} pendingLabel="Guardando...">
            Guardar cambios
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Modal de Invitación ──────────────────────────────────────────────────────
function InviteUserModal({ onClose }: { onClose: () => void }) {
  const { data: rolesData } = useRoles()
  const { mutate: invite, isPending, error } = useInviteUser()
  const { canInviteWithoutPassword, forcesSSOOnly } = useTenantSSOEnabled()
  const [ssoOnly, setSsoOnly] = useState(() => forcesSSOOnly || canInviteWithoutPassword)

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { role_ids: [] },
  })

  const selectedRoles = watch('role_ids')

  const toggleRole = (roleId: number) => {
    const current = selectedRoles || []
    if (current.includes(roleId)) {
      setValue('role_ids', current.filter((r) => r !== roleId))
    } else {
      setValue('role_ids', [...current, roleId])
    }
  }

  const onSubmit = (data: InviteFormData) => {
    invite({ ...data, sso_only: ssoOnly }, { onSuccess: onClose })
  }

  const errorMessage = error
    ? (error as any)?.response?.data?.error?.message || 'Error al invitar al usuario.'
    : null

  return (
    <Modal open onClose={onClose} title="Invitar usuario" subtitle="Se enviará un email con instrucciones de acceso" size="md" dismissOnScrimClick={false}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {errorMessage && (
          <div className="p-3 bg-danger-surface border border-danger-border rounded-lg text-danger-content text-sm">
            {errorMessage}
          </div>
        )}

        <div>
          <label htmlFor="userspage-email" className="block text-sm font-medium text-content mb-1.5">
            Email <span className="text-danger-solid">*</span>
          </label>
          <input id="userspage-email"
            {...register('email')}
            type="email"
            placeholder="usuario@empresa.com"
            className="w-full px-4 py-2.5 border border-divider-strong rounded-lg text-sm
              text-content placeholder:text-content-subtle outline-none focus:ring-2
              focus:ring-primary focus:border-primary"
          />
          {errors.email && <p className="mt-1 text-xs text-danger-content-soft">{errors.email.message}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="userspage-first-name" className="block text-sm font-medium text-content mb-1.5">Nombre</label>
            <input id="userspage-first-name"
              {...register('first_name')}
              type="text"
              placeholder="María"
              className="w-full px-4 py-2.5 border border-divider-strong rounded-lg text-sm
                text-content placeholder:text-content-subtle outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="userspage-last-name" className="block text-sm font-medium text-content mb-1.5">Apellido</label>
            <input id="userspage-last-name"
              {...register('last_name')}
              type="text"
              placeholder="García"
              className="w-full px-4 py-2.5 border border-divider-strong rounded-lg text-sm
                text-content placeholder:text-content-subtle outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-content mb-2">
            Roles <span className="text-danger-solid">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(rolesData || []).filter(r => r.role_type !== 'admin').map((role) => (
              <label
                key={role.id}
                className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors
                  ${selectedRoles?.includes(role.id)
                    ? 'border-primary bg-primary-50'
                    : 'border-divider hover:border-divider-strong hover:bg-soft'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={selectedRoles?.includes(role.id) ?? false}
                  onChange={() => toggleRole(role.id)}
                  className="w-4 h-4 text-primary rounded"
                />
                <span className="text-sm text-content">{role.name}</span>
              </label>
            ))}
          </div>
          {errors.role_ids && (
            <p className="mt-1 text-xs text-danger-content-soft">{errors.role_ids.message}</p>
          )}
        </div>

        {(canInviteWithoutPassword || forcesSSOOnly) && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-glass-3 border border-divider">
            <input
              type="checkbox"
              id="sso-only"
              checked={ssoOnly}
              onChange={(e) => setSsoOnly(e.target.checked)}
              disabled={forcesSSOOnly}
              className="mt-1 accent-primary"
            />
            <label htmlFor="sso-only" className="text-sm cursor-pointer">
              <div className="font-medium text-content">Crear como cuenta SSO (sin contraseña)</div>
              <div className="text-content-muted text-xs mt-0.5">
                {forcesSSOOnly
                  ? 'La empresa requiere acceso SSO. Esta opción no se puede desactivar.'
                  : 'El usuario activará su cuenta iniciando sesión con SSO. Recomendado cuando todos tus usuarios usan Google/SAML.'}
              </div>
            </label>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-divider text-content-muted rounded-lg text-sm
              font-medium hover:bg-soft transition-colors"
          >
            Cancelar
          </button>
          <Button type="submit" size="lg" fullWidth isPending={isPending} pendingLabel="Invitando...">
            Enviar invitación
          </Button>
        </div>
      </form>
    </Modal>
  )
}
